/**
 * Reporte Crediticio — migración web de ares7040 (escritorio).
 *
 * Reutiliza el endpoint existente /api/ares4030_crediticio (los mismos datos
 * de reimprimirfacturaunica: cabecera del préstamo, amortización e incautos)
 * y calcula EN EL FRONT los agregados que el escritorio armaba a mano:
 * capital/interés/mora pagados y, para las cuotas con fecha vencida, el
 * monto en atraso Σ((capital−vpagcap)+(interes−vpagint)) y la mora en
 * atraso Σ(pendiente×(mora%/100/30)×díasAtraso) con el % de mora del
 * préstamo que devuelve el endpoint (moraprestamo).
 *
 * El selector se llena con /api/ares4030_prestamos, que trae TODOS los
 * préstamos: el crediticio de un préstamo cancelado (status 'C', que incluye
 * los ya saldados) se pide igual que el de uno activo.
 *
 * OJO: el escritorio tenía la lógica de rol INVERTIDA (le negaba la pantalla
 * justo al rol que tenía el permiso). Aquí no se replica ninguna
 * comprobación de rol en el cliente: el backend ya filtra por permiso.
 */
import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useGlobalMDAlert } from "context";
import FotoCliente from "components/clientes/FotoCliente";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";

const filtrarPrestamos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string"
      ? o
      : `${o.noprest} ${o.nombres || ""} ${o.cedula || ""} ${o.chasis || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const money = (value) => fmtMoney(value);

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

// Las fechas del backend vienen "dd-mm-yyyy".
const parseFecha = (s) => {
  const [d, m, y] = String(s || "")
    .split(/[-/]/)
    .map((x) => parseInt(x, 10));
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
};

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const chipInfo = { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 };

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function ResumenChip({ label, value, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 130,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>{value}</Typography>
    </Box>
  );
}

// Documento crediticio completo: compañía + préstamo + agregados + amortización.
function imprimirCrediticio(compania, p, filas, agregados, incautos, fotoB64) {
  const filasAmort = filas
    .map(
      (a) => `<tr>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${a.nocuota}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${a.fecha}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.capital
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.interes
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.vpagcap
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.vpagint
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.vpagmora
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          a.pendiente
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:center">${a.estado}</td>
      </tr>`
    )
    .join("");
  const filasIncautos = (incautos || [])
    .map(
      (x) => `<tr>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${esc(x.chasis)}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd;text-align:right">${fmtMoney(
          x.valor
        )}</td>
        <td style="padding:3px 6px;border-bottom:1px solid #ddd">${esc(x.incautador)}</td>
      </tr>`
    )
    .join("");
  const vehiculo = [p.marca, p.modelo, p.colore].filter(Boolean).join(" ");
  const html = `
    <html><head><meta name="color-scheme" content="light"><title>Reporte Crediticio ${esc(p.noprest)}</title>
    <style>.cierre{margin-top:auto;page-break-inside:avoid}</style></head>
    <body style="background:#fff;color:#000;font-family:Arial,sans-serif;padding:18px;font-size:12px;margin:0;box-sizing:border-box;display:flex;flex-direction:column;min-height:96vh">
      <h3 style="text-align:center;margin:0 0 2px">${esc(compania?.nombre || "")}</h3>
      <p style="text-align:center;margin:0">${esc(compania?.direccion || "")}</p>
      <p style="text-align:center;margin:0 0 10px">${
        compania?.rnc ? `RNC: ${esc(compania.rnc)}` : ""
      }</p>
      <p style="text-align:center;margin:0 0 14px;font-weight:bold;font-size:14px">
        Reporte Crediticio — Préstamo #${esc(p.noprest)}${
    String(p.status || "").toUpperCase() === "C" ? " (CANCELADO)" : ""
  }
      </p>
      <div style="display:flex;gap:14px;align-items:flex-start;margin-bottom:12px">
      ${
        fotoB64
          ? `<img src="data:image/jpeg;base64,${fotoB64}" alt="" style="width:110px;height:130px;object-fit:cover;border:1px solid #000;flex:none"/>`
          : ""
      }
      <table style="flex:1;width:100%;font-size:12px">
        <tr><td><b>Cliente:</b> ${esc(p.nombres)}</td><td><b>Cédula:</b> ${esc(p.cedula)}</td></tr>
        <tr><td><b>Dirección:</b> ${esc(
          [p.direccion, p.urb].filter(Boolean).join(", ")
        )}</td><td><b>Teléfono:</b> ${esc(p.telefono)}</td></tr>
        <tr><td><b>Vehículo:</b> ${esc(vehiculo) || "—"}</td><td><b>Chasis:</b> ${
    esc(p.chasis) || "—"
  }</td></tr>
        <tr><td><b>Fecha:</b> ${esc(p.fecha)}</td><td><b>Plazo:</b> ${esc(p.plazo)}</td></tr>
        <tr><td><b>Financiado:</b> RD$ ${fmtMoney(p.cantsol)}</td>
            <td><b>Interés:</b> ${fmtMoney(p.porinteres)} % · <b>Mora:</b> ${fmtMoney(
    p.moraprestamo
  )} %</td></tr>
        <tr><td><b>Último pago:</b> ${esc(p.fultpago) || "—"} (RD$ ${fmtMoney(
    p.montoult
  )})</td><td><b>Balance:</b> RD$ ${fmtMoney(p.balance)}</td></tr>
        ${
          p.fiador
            ? `<tr><td><b>Garante:</b> ${esc(p.fiador)} · ${esc(
                p.fiadorced
              )}</td><td><b>Tel. garante:</b> ${esc(p.fiadortel)}</td></tr>`
            : ""
        }
      </table>
      </div>
      <p style="margin:0 0 10px;font-size:12px;border:1px solid #000;padding:6px">
        <b>Capital pagado:</b> RD$ ${fmtMoney(agregados.capitalPagado)} ·
        <b>Interés pagado:</b> RD$ ${fmtMoney(agregados.interesPagado)} ·
        <b>Mora pagada:</b> RD$ ${fmtMoney(agregados.moraPagada)}<br/>
        <b>Cuotas en atraso:</b> ${agregados.cuotasAtrasadas} ·
        <b>Monto en atraso:</b> RD$ ${fmtMoney(agregados.montoAtraso)} ·
        <b>Mora en atraso:</b> RD$ ${fmtMoney(agregados.moraAtraso)}
      </p>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        <thead><tr style="background:#eee">
          <th style="padding:4px 6px;text-align:left">Cuota</th>
          <th style="padding:4px 6px;text-align:left">Fecha</th>
          <th style="padding:4px 6px;text-align:right">Capital</th>
          <th style="padding:4px 6px;text-align:right">Interés</th>
          <th style="padding:4px 6px;text-align:right">Cap. pagado</th>
          <th style="padding:4px 6px;text-align:right">Int. pagado</th>
          <th style="padding:4px 6px;text-align:right">Mora pagada</th>
          <th style="padding:4px 6px;text-align:right">Pendiente</th>
          <th style="padding:4px 6px;text-align:center">Estado</th>
        </tr></thead>
        <tbody>${filasAmort}</tbody>
      </table>
      ${
        filasIncautos
          ? `<p style="margin:12px 0 4px;font-weight:bold">Incautos activos</p>
             <table style="width:100%;border-collapse:collapse;font-size:11px">
               <thead><tr style="background:#eee">
                 <th style="padding:4px 6px;text-align:left">Chasis</th>
                 <th style="padding:4px 6px;text-align:right">Valor</th>
                 <th style="padding:4px 6px;text-align:left">Incautador</th>
               </tr></thead>
               <tbody>${filasIncautos}</tbody>
             </table>`
          : ""
      }
      <div class="cierre">
        <p style="margin:18px 0 0;font-size:11px">
          Declaro que he recibido y revisado el presente estado de mi financiamiento, estoy conforme con
          los balances aquí indicados y me comprometo a pagar las cuotas pendientes en las fechas
          establecidas.
        </p>
        <table style="width:100%;margin-top:55px;font-size:11px;text-align:center">
          <tr>
            <td style="width:45%;border-top:1px solid #000;padding-top:5px">
              ${esc(p.nombres) || "Cliente"}<br/>${esc(p.cedula) || ""}
            </td>
            <td style="width:10%"></td>
            <td style="width:45%;border-top:1px solid #000;padding-top:5px">Por ${esc(
              compania?.nombre || "la empresa"
            )}</td>
          </tr>
        </table>
      </div>
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      /* noop */
    }
    setTimeout(() => iframe.remove(), 60000);
  }, 200);
}

export default function ReporteCrediticio({ noprestInicial } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [prestamos, setPrestamos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [prestamo, setPrestamo] = useState(null); // opción del autocomplete
  const [datos, setDatos] = useState(null); // respuesta de ares4030_crediticio
  const [buscando, setBuscando] = useState(false);
  const [compania, setCompania] = useState(null);

  useEffect(() => {
    let activo = true;
    setCargando(true);
    // ares4030_prestamos y no el listado de refinanciamiento: ese solo trae
    // los activos (status='A'), así que un préstamo cancelado —incluidos los
    // que ya terminaron de pagarse— no aparecía y su crediticio no salía.
    // De paso queda bajo el mismo permiso (ares4030) que el endpoint de datos.
    post("/api/ares4030_prestamos", {})
      .then(({ ok, data }) => {
        if (!activo) return;
        if (ok) setPrestamos(data.prestamos || []);
        else
          showAlert({
            message: data.Error || "No se pudieron cargar los préstamos",
            color: "error",
          });
      })
      .finally(() => activo && setCargando(false));
    post("/api/cobropago_perfil", {}).then(({ ok, data }) => {
      if (activo && ok) setCompania(data.compania || null);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiar = () => {
    setPrestamo(null);
    setDatos(null);
  };

  // Preselección: al abrir desde el expediente ya viene el préstamo elegido.
  const [preselListo, setPreselListo] = useState(false);
  useEffect(() => {
    if (!noprestInicial || preselListo || !prestamos.length) return;
    const opcion = prestamos.find((x) => String(x.noprest) === String(noprestInicial));
    if (opcion) elegirPrestamo(opcion);
    setPreselListo(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prestamos]);

  const elegirPrestamo = async (p) => {
    setPrestamo(p);
    setDatos(null);
    if (!p) return;
    setBuscando(true);
    try {
      const { ok, data } = await post("/api/ares4030_crediticio", { noprest: p.noprest });
      if (!ok) {
        showAlert({ message: data.Error || "No se pudo recuperar el crediticio", color: "error" });
        setPrestamo(null);
        return;
      }
      setDatos(data);
    } finally {
      setBuscando(false);
    }
  };

  const cab = datos?.prestamo || null;

  // Filas de la amortización con pendiente y estado calculados en el front.
  const filas = useMemo(() => {
    if (!datos) return [];
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return (datos.amortizacion || []).map((a) => {
      const pendiente = Math.max(
        0,
        toNum(a.capital) - toNum(a.vpagcap) + (toNum(a.interes) - toNum(a.vpagint)) - toNum(a.descinte)
      );
      const f = parseFecha(a.fecha);
      const vencida = !!f && f < hoy;
      let estado = "Pendiente";
      if ((a.status || "").trim().toUpperCase() !== "A") estado = "Pagada";
      else if (vencida && pendiente > 0.005) estado = "Atrasada";
      return { id: a.nocuota, ...a, pendiente, vencida, estado };
    });
  }, [datos]);

  // Agregados calculados en el front (el endpoint solo trae la amortización):
  // pagados = sumas de vpag*; atraso = cuotas con fecha < hoy y pendiente > 0;
  // mora en atraso = pendiente × (mora%/100/30) × días de atraso.
  const agregados = useMemo(() => {
    if (!cab) return null;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const moraPct = toNum(cab.moraprestamo);
    let capitalPagado = 0;
    let interesPagado = 0;
    let moraPagada = 0;
    let montoAtraso = 0;
    let moraAtraso = 0;
    let cuotasAtrasadas = 0;
    filas.forEach((a) => {
      capitalPagado += toNum(a.vpagcap);
      interesPagado += toNum(a.vpagint);
      moraPagada += toNum(a.vpagmora);
      if (!a.vencida || a.pendiente <= 0.005) return;
      cuotasAtrasadas += 1;
      montoAtraso += a.pendiente;
      const f = parseFecha(a.fecha);
      const dias = Math.floor((hoy - f) / 86400000);
      moraAtraso += a.pendiente * (moraPct / 100 / 30) * dias;
    });
    return { capitalPagado, interesPagado, moraPagada, montoAtraso, moraAtraso, cuotasAtrasadas };
  }, [cab, filas]);

  const columns = [
    { field: "nocuota", headerName: "Cuota", width: 80 },
    { field: "fecha", headerName: "Fecha", width: 110 },
    {
      field: "capital",
      headerName: "Capital",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "interes",
      headerName: "Interés",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "vpagcap",
      headerName: "Cap. pagado",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "vpagint",
      headerName: "Int. pagado",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "vpagmora",
      headerName: "Mora pagada",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "pendiente",
      headerName: "Pendiente",
      width: 115,
      align: "right",
      headerAlign: "right",
      valueFormatter: money,
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 115,
      renderCell: (p) => {
        const estilos = {
          Pagada: { bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 },
          Atrasada: { bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 700 },
          Pendiente: {
            bgcolor: FP.color.surfaceAlt,
            color: FP.color.textSecondary,
            fontWeight: 700,
          },
        };
        return <Chip size="small" label={p.row.estado} sx={estilos[p.row.estado]} />;
      },
    },
  ];

  const imprimir = () => {
    if (!cab || !agregados) return;
    imprimirCrediticio(compania, cab, filas, agregados, datos.incautos || [], datos.foto_b64 || "");
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Reporte Crediticio"
        subtitle="Historial del préstamo con amortización, pagos y atrasos (ares7040)"
        cta={
          <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
            Nuevo
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Préstamo */}
        <Box sx={sectionBox}>
          <SectionTitle icon={PersonOutlineIcon} color={FP.color.accentBlue}>
            Préstamo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarPrestamos}
                options={prestamos}
                loading={cargando || buscando}
                value={prestamo}
                getOptionLabel={(o) =>
                  typeof o === "string" ? o : `${o.noprest} — ${o.nombres || ""}`
                }
                isOptionEqualToValue={(o, v) => (o.noprest || o) === (v.noprest || v)}
                onChange={(_, v) => elegirPrestamo(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.noprest}>
                    <Box>
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          {o.noprest} — {o.nombres}
                        </Typography>
                        {/* 'C' = cancelado, que aquí incluye los ya saldados */}
                        {String(o.status || "").toUpperCase() === "C" && (
                          <Chip
                            size="small"
                            label="Cancelado"
                            sx={{
                              height: 18,
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              bgcolor: FP.color.surfaceAlt,
                              color: FP.color.textSecondary,
                            }}
                          />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.cedula, o.chasis, o.sucursal].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Préstamo"
                    helperText="Busca por número, nombre, cédula o chasis"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {cab && (
              <Grid item xs={12} md={7} sx={{ display: "flex", alignItems: "center", gap: 1.6 }}>
                <FotoCliente
                  cedula={cab.cedula}
                  nombre={cab.nombres}
                  fotoB64={datos.foto_b64 || ""}
                  size={72}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={cab.nombres} sx={chipInfo} />
                  <Chip label={`Cédula ${cab.cedula || "—"}`} sx={chipInfo} />
                  {(cab.marca || cab.modelo) && (
                    <Chip
                      label={[cab.marca, cab.modelo, cab.colore].filter(Boolean).join(" · ")}
                      sx={chipInfo}
                    />
                  )}
                  {cab.chasis && <Chip label={`Chasis ${cab.chasis}`} sx={chipInfo} />}
                  <Chip label={`Fecha ${cab.fecha || "—"}`} sx={chipInfo} />
                  <Chip label={`Plazo ${cab.plazo || "—"}`} sx={chipInfo} />
                  <Chip label={`Interés ${fmtMoney(cab.porinteres)} %`} sx={chipInfo} />
                  <Chip label={`Mora ${fmtMoney(cab.moraprestamo)} %`} sx={chipInfo} />
                  <Chip label={`Sucursal ${cab.sucursal || "—"}`} sx={chipInfo} />
                  {String(cab.status || "").toUpperCase() === "C" && (
                    <Chip label="Préstamo cancelado" sx={chipInfo} />
                  )}
                  <Chip
                    label={`Balance: RD$ ${fmtMoney(cab.balance)}`}
                    sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Crediticio */}
        <Box sx={{ ...sectionBox, mb: 0 }}>
          <SectionTitle icon={ReceiptLongOutlinedIcon} color={FP.color.success}>
            Historial Crediticio
          </SectionTitle>

          {!cab && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 8, color: FP.color.textMuted, bgcolor: "#fff", borderRadius: 2 }}
            >
              {buscando ? (
                <CircularProgress size={28} />
              ) : (
                <>
                  <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
                  <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
                    Seleccione un préstamo para ver su reporte crediticio
                  </Typography>
                </>
              )}
            </Stack>
          )}

          {cab && agregados && (
            <>
              <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
                <ResumenChip
                  label="CAPITAL PAGADO"
                  value={`RD$ ${fmtMoney(agregados.capitalPagado)}`}
                  color={FP.color.success}
                />
                <ResumenChip
                  label="INTERÉS PAGADO"
                  value={`RD$ ${fmtMoney(agregados.interesPagado)}`}
                  color={FP.color.success}
                />
                <ResumenChip
                  label="MORA PAGADA"
                  value={`RD$ ${fmtMoney(agregados.moraPagada)}`}
                  color={FP.color.success}
                />
                <ResumenChip
                  label="CUOTAS EN ATRASO"
                  value={String(agregados.cuotasAtrasadas)}
                  color={agregados.cuotasAtrasadas ? FP.color.danger : FP.color.navy}
                />
                <ResumenChip
                  label="MONTO EN ATRASO"
                  value={`RD$ ${fmtMoney(agregados.montoAtraso)}`}
                  color={agregados.montoAtraso ? FP.color.danger : FP.color.navy}
                />
                <ResumenChip
                  label="MORA EN ATRASO"
                  value={`RD$ ${fmtMoney(agregados.moraAtraso)}`}
                  color={agregados.moraAtraso ? FP.color.danger : FP.color.navy}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  startIcon={<PrintOutlinedIcon />}
                  onClick={imprimir}
                  sx={btnOutline(FP.color.navy)}
                >
                  Imprimir
                </Button>
              </Stack>

              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={filas}
                  columns={columns}
                  rowHeight={42}
                  hideFooter={filas.length <= 25}
                  pageSizeOptions={[25, 50, 100]}
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  sx={fpDataGridSx}
                  disableRowSelectionOnClick
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
